import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { getVesselSpeedFuelConsumption } from "../../../../services/vesselMachineryService";
import { generateDateTicks } from "../../../../services/dataTicks";

const SpeedFuelConsumption = ({ voyageId, selectedEngine }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedEngineId } = useSelector((state) => state.mainEngine);
  console.log("selectedEngine:", selectedEngine);

  const vesselId = currentVessel?._id;

  // Fetch data from API
  const fetchData = async (start, end) => {
    if (!vesselId || !selectedEngineId || !start || !end) return;
    console.log("selectedEngineId:", selectedEngineId);
    const engineId = selectedEngine.toUpperCase();

    setLoading(true);
    try {
      console.log("Speed vs Fuel:");
      const data = await getVesselSpeedFuelConsumption(
        vesselId,
        engineId,
        voyageId ? null : start,
        voyageId ? null : end,
        voyageId || null
      );
      setChartData(data);
    } catch (error) {
      console.error("Error fetching speed/fuel data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  }, [vesselId, selectedEngine, startDate, endDate, voyageId]);

  // Prepare chart series data - both as scatter
  const consumptionSeries =
    chartData?.map((point) => [
      new Date(point.date).getTime(),
      point.consumption,
    ]) || [];

  const speedSeries =
    chartData?.map((point) => [new Date(point.date).getTime(), point.speed]) ||
    [];

  // Proper trend line calculation
  const calculateCombinedTrendLine = () => {
    if (chartData.length < 2) return [];

    // Calculate efficiency ratio (consumption per unit speed) over time
    const efficiencyData = chartData
      .filter((point) => point.speed > 0)
      .map((point, index) => {
        const efficiency = point.consumption / point.speed;
        return [
          new Date(point.date).getTime(), // timestamp
          efficiency,
        ];
      });

    if (efficiencyData.length < 2) return [];

    // Linear regression on efficiency over time
    const n = efficiencyData.length;
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumX2 = 0;

    efficiencyData.forEach((point, index) => {
      const x = index; // time index
      const y = point[1]; // efficiency value
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Create trend line for efficiency
    return efficiencyData.map((point, index) => [
      point[0], // original timestamp
      slope * index + intercept, // trend efficiency value
    ]);
  };

  const combinedTrendData = calculateCombinedTrendLine();

  const isChartLoading = loading;

  const dates = chartData ? chartData.map((item) => item.date) : [];
  let tickPositions = generateDateTicks(dates);

  const chartOptions = {
    chart: {
      type: "scatter",
      height: "500px",
      backgroundColor: "transparent",
      spacing: [20, 15, 15, 15],
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: "{value:%b %e}",
        style: {
          color: "#666666",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
    },
    yAxis: [
      {
        title: {
          text: "Avg ME Consumption (MT/day)",
          style: {
            color: "#1a5276",
            fontSize: "12px",
          },
        },
        min: 0,
        gridLineWidth: 1,
        gridLineColor: "#f0f0f0",
        labels: {
          style: {
            color: "#666666",
          },
        },
      },
      {
        title: {
          text: "Speed (kts)",
          style: {
            color: "#27ae60",
            fontSize: "12px",
          },
        },
        min: 0,
        opposite: true,
        gridLineWidth: 0,
        labels: {
          style: {
            color: "#666666",
          },
        },
      },
    ],
    legend: {
      align: "right",
      verticalAlign: "top",
      itemStyle: {
        color: "#666666",
        fontWeight: "normal",
      },
      itemHoverStyle: {
        color: "#333333",
      },
      symbolRadius: 0,
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 6,
      shadow: true,
      useHTML: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      pointFormat:
        '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y}</span></div>',
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          symbol: "circle",
          lineWidth: 2,
          lineColor: "#ffffff",
          states: {
            hover: {
              enabled: true,
              lineColor: "#333333",
              radius: 6,
            },
          },
        },
        states: {
          hover: {
            halo: {
              size: 8,
            },
          },
        },
      },
      line: {
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            halo: {
              size: 0,
            },
          },
        },
      },
    },
    series: [
      {
        name: "Fuel Consumption",
        type: "scatter",
        data: consumptionSeries,
        color: "#1a5276",
        yAxis: 0,
        marker: {
          fillColor: "#1a5276",
          lineWidth: 2,
          lineColor: "#ffffff",
          radius: 5,
        },
        tooltip: {
          valueSuffix: " MT/day",
        },
      },
      {
        name: "Speed",
        type: "scatter",
        data: speedSeries,
        color: "#27ae60",
        yAxis: 1,
        marker: {
          fillColor: "#27ae60",
          lineWidth: 2,
          lineColor: "#ffffff",
          radius: 5,
        },
        tooltip: {
          valueSuffix: " kts",
        },
      },
      {
        name: "Performance Trend",
        type: "line",
        data: combinedTrendData,
        color: "#e74c3c",
        yAxis: 0,
        lineWidth: 2,
        dashStyle: "Dash",
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        tooltip: {
          valueSuffix: " units",
          pointFormat:
            '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y:.2f}</span></div>',
        },
      },
    ],
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            legend: {
              align: "center",
              verticalAlign: "bottom",
              layout: "horizontal",
            },
            xAxis: {
              labels: {
                format: "{value:%b %e}",
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex justify-between items-center">
        <h1>Avg ME Cons. over Perf. Speed</h1>
      </div>

      {/* Chart */}
      <div className="relative">
        {isChartLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          containerProps={{
            className: "w-full",
            style: { height: "500px" },
          }}
        />
      </div>
    </div>
  );
};

export default SpeedFuelConsumption;
